import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

export const useAuthButton = () => {
  const { user, isLoading: loading, signOut, session } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { mutateAsync: signOutMutation } = signOut;
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(false);
  const adminCheckComplete = useRef(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  useEffect(() => {
    let isMounted = true;

    const checkAdminStatus = async () => {
      if (user && !isCheckingAdmin && !adminCheckComplete.current) {
        try {
          setIsCheckingAdmin(true);
          console.log("Checking admin status for user:", user.id);

          const { data, error } = await supabase
            .from("profiles")
            .select("is_admin")
            .eq("id", user.id)
            .single();

          if (error) {
            console.error("Error checking admin status:", error);
            if (isMounted) {
              setIsCheckingAdmin(false);
            }
            return;
          }

          if (data && isMounted) {
            console.log("Admin status result:", data);
            setIsAdmin(!!data.is_admin);
            adminCheckComplete.current = true;
          } else if (isMounted) {
            console.log("No profile data found for user");
            setIsAdmin(false);
            adminCheckComplete.current = true;
          }
        } catch (err) {
          console.error("Exception checking admin status:", err);
          if (isMounted) {
            setIsAdmin(false);
            adminCheckComplete.current = true;
          }
        } finally {
          if (isMounted) {
            setIsCheckingAdmin(false);
          }
        }
      } else if (!user && isMounted) {
        setIsAdmin(false);
        adminCheckComplete.current = false;
      }
    };

    if (!loading) {
      checkAdminStatus();
    }

    return () => {
      isMounted = false;
    };
  }, [user, loading, isCheckingAdmin]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await signOutMutation();
      // Reset admin check state
      adminCheckComplete.current = false;
      setIsAdmin(false);
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account",
      });
      navigate("/auth");
    } catch (error) {
      console.error("Error during logout:", error);
      // Still reset states even if there was an error
      adminCheckComplete.current = false;
      toast({
        title: "Error signing out",
        description: "There was a problem signing out of your account",
        variant: "destructive",
      });
      setIsAdmin(false);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleRetry = () => {
    window.location.reload();
  };

  return {
    user,
    loading,
    isLoggingOut,
    handleLogout,
    handleRetry,
    isAdmin,
    session,
  };
};
